import * as React from "react"
import { cn } from "../lib/utils"
import { convertTimeToTimezone, getTimezoneDisplay, getTimezoneForState } from "@joinomu/shared"
import { Button } from "./button"
import { Input } from "./input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table"
import { Badge } from "./badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip"
import { Search, UserPlus } from "lucide-react"

export interface Patient {
  id: string
  profile_id: string
  first_name: string
  last_name: string
  email: string
  phone?: string
  date_of_birth?: string
  has_completed_intake?: boolean
  treatment_type?: string
  assigned_date?: string
  is_primary?: boolean
  assigned_providers?: string[]
  treatment_types?: string[]
  medications?: string[]
  created_at: string
  timezone?: string  // Patient's timezone
  // Subscription fields for billing
  subscription_plan?: string
  subscription_status?: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'unpaid'
  current_period_end?: string
  stripe_customer_id?: string
  // Visit/appointment data
  previous_appointment?: {
    appointment_date: string
    start_time?: string
    status?: string
    provider_timezone?: string  // Timezone where appointment was scheduled
  } | null
  next_appointment?: {
    appointment_date: string
    start_time?: string
    status?: string
    provider_timezone?: string  // Timezone where appointment was scheduled
  } | null
  // Eligibility/Onboarding fields
  treatment_preferences?: string[] | string
  weight_loss_goals?: string
  medication_preference?: string
  selected_state?: string
  gender?: string
  motivations?: string[] | string
  height_feet?: number
  height_inches?: number
  weight?: number
  bmi?: number
  activity_level?: string
  eating_disorders?: string[] | string
  mental_health?: string[] | string
  self_harm_screening?: string[] | string
  diagnosed_conditions?: string[] | string
  chronic_diseases?: string
  family_medical_history?: string[] | string
  family_health?: string
  medication_history?: string[] | string
  procedures?: string
  supplements?: string
  allergies?: string[] | string
  drinking?: string
  drugs?: string[] | string
  smoking?: string
  heart_rate?: string
  gastrointestinal?: string[] | string
  side_effects?: string[] | string
  side_effect_guidance?: string[] | string
  challenges?: string[] | string
  challenges_elaborate?: string
  program_adherence?: string[] | string
  program_consistency?: string[] | string
  gastrointestinal_dosing?: string
  energy_dosing?: string
  muscle_loss_dosing?: string
  additional_info?: string
}

export interface PatientTableProps {
  patients: Patient[]
  loading?: boolean
  searchPlaceholder?: string
  showSearch?: boolean
  showAddButton?: boolean
  addButtonLabel?: string
  onAddPatient?: () => void
  onPatientClick?: (patient: Patient) => void
  emptyStateText?: string
  className?: string
  title?: string
  subtitle?: string
  showResultsCount?: boolean
}

export function PatientTable({
  patients,
  loading = false,
  searchPlaceholder = "Search patients...",
  showSearch = true,
  showAddButton = false,
  addButtonLabel = "Add Patient",
  onAddPatient,
  onPatientClick,
  emptyStateText = "No patients found.",
  className,
  title,
  subtitle,
  showResultsCount = true,
}: PatientTableProps) {
  const [searchTerm, setSearchTerm] = React.useState("")
  const [pageSize, setPageSize] = React.useState(8)
  const [currentPage, setCurrentPage] = React.useState(0)
  const [tableWidth, setTableWidth] = React.useState<number>(600)
  const mainContainerRef = React.useRef<HTMLDivElement>(null)

  const filteredPatients = React.useMemo(() => {
    if (!searchTerm) return patients

    const term = searchTerm.toLowerCase()
    return patients.filter(
      (patient) =>
        patient.first_name?.toLowerCase().includes(term) ||
        patient.last_name?.toLowerCase().includes(term) ||
        patient.email?.toLowerCase().includes(term) ||
        patient.phone?.toLowerCase().includes(term)
    )
  }, [patients, searchTerm])

  // Pagination logic
  const totalPages = Math.ceil(filteredPatients.length / pageSize)
  const paginatedPatients = React.useMemo(() => {
    const startIndex = currentPage * pageSize
    return filteredPatients.slice(startIndex, startIndex + pageSize)
  }, [filteredPatients, currentPage, pageSize])

  // Reset to first page when search changes
  React.useEffect(() => {
    setCurrentPage(0)
  }, [searchTerm])

  // Calculate available width and set table width to full width
  React.useEffect(() => {
    let rafId: number

    const calculateTableWidth = () => {
      if (mainContainerRef.current) {
        // Get the full available width
        const containerWidth = mainContainerRef.current.offsetWidth
        const minWidth = 800 // Minimum width for table readability
        
        // Use full available width (no padding deduction for full width)
        const calculatedWidth = Math.max(minWidth, containerWidth)
        setTableWidth(calculatedWidth)
      }
    }

    const debouncedCalculate = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(calculateTableWidth)
    }

    // Initial calculation with multiple attempts
    calculateTableWidth()
    setTimeout(calculateTableWidth, 0)
    setTimeout(calculateTableWidth, 100)

    // Use ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(debouncedCalculate)

    if (mainContainerRef.current) {
      resizeObserver.observe(mainContainerRef.current)
    }

    // Multiple event listeners for maximum responsiveness
    window.addEventListener('resize', debouncedCalculate)
    window.addEventListener('orientationchange', debouncedCalculate)
    document.addEventListener('fullscreenchange', debouncedCalculate)
    
    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      resizeObserver.disconnect()
      window.removeEventListener('resize', debouncedCalculate)
      window.removeEventListener('orientationchange', debouncedCalculate)
      document.removeEventListener('fullscreenchange', debouncedCalculate)
    }
  }, [])

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  const formatName = (first_name: string, last_name: string) => {
    return `${first_name} ${last_name}`.trim() || "Unknown"
  }

  const getIntakeStatus = (has_completed_intake?: boolean) => {
    if (has_completed_intake === true) {
      return <Badge variant="default">Complete</Badge>
    } else if (has_completed_intake === false) {
      return <Badge variant="secondary">Pending</Badge>
    }
    return <Badge variant="outline">Unknown</Badge>
  }

  const getTreatmentBadges = (patient: Patient) => {
    // For individual assignments (provider view)
    if (patient.treatment_type) {
      return (
        <Badge variant="outline" className="capitalize">
          {patient.treatment_type.replace('_', ' ')}
        </Badge>
      )
    }
    
    // For multiple treatments (admin view)
    if (patient.treatment_types && patient.treatment_types.length > 0) {
      return (
        <div className="flex flex-wrap gap-1">
          {patient.treatment_types.slice(0, 2).map((type, index) => (
            <Badge key={index} variant="outline" className="capitalize text-xs">
              {type.replace('_', ' ')}
            </Badge>
          ))}
          {patient.treatment_types.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{patient.treatment_types.length - 2}
            </Badge>
          )}
        </div>
      )
    }
    
    return <span className="text-muted-foreground text-sm">None</span>
  }

  const getProvidersList = (assigned_providers?: string[]) => {
    if (!assigned_providers || assigned_providers.length === 0) {
      return <span className="text-muted-foreground text-sm">Unassigned</span>
    }
    
    if (assigned_providers.length === 1) {
      return <span className="text-sm">{assigned_providers[0]}</span>
    }
    
    return (
      <div className="flex flex-col gap-1">
        <span className="text-sm">{assigned_providers[0]}</span>
        {assigned_providers.length > 1 && (
          <span className="text-muted-foreground text-xs">
            +{assigned_providers.length - 1} more
          </span>
        )}
      </div>
    )
  }

  const getMedicationsList = (medications?: string[]) => {
    if (!medications || medications.length === 0) {
      return <span className="text-muted-foreground text-sm">None</span>
    }
    
    if (medications.length === 1) {
      return (
        <Badge variant="secondary" className="text-xs">
          {medications[0]}
        </Badge>
      )
    }
    
    return (
      <div className="flex flex-wrap gap-1">
        {medications.slice(0, 2).map((medication, index) => (
          <Badge key={index} variant="secondary" className="text-xs">
            {medication}
          </Badge>
        ))}
        {medications.length > 2 && (
          <Badge variant="outline" className="text-xs">
            +{medications.length - 2}
          </Badge>
        )}
      </div>
    )
  }

  const formatAppointment = (
    appointment?: { 
      appointment_date: string; 
      start_time?: string; 
      status?: string;
      provider_timezone?: string;
    } | null,
    patientTimezone?: string
  ) => {
    if (!appointment) {
      return <span className="text-muted-foreground text-sm">No visits</span>
    }

    // Parse the appointment date properly for timezone conversion
    let appointmentDateTime: Date;
    
    if (appointment.start_time) {
      // Combine date and time for accurate timezone conversion
      appointmentDateTime = new Date(`${appointment.appointment_date}T${appointment.start_time}`);
    } else {
      appointmentDateTime = new Date(appointment.appointment_date);
    }

    let displayTime = appointment.start_time;
    let displayTimezone = '';
    
    // Convert time to patient timezone if both provider timezone and patient timezone are available
    if (appointment.start_time && appointment.provider_timezone && patientTimezone) {
      try {
        const convertedTime = convertTimeToTimezone(
          appointment.start_time,
          appointment.provider_timezone,
          patientTimezone,
          appointment.appointment_date
        );
        displayTime = convertedTime;
        displayTimezone = getTimezoneDisplay(patientTimezone);
        
        // Check if the date changed due to timezone conversion
        const originalDate = new Date(`${appointment.appointment_date}T${appointment.start_time}`);
        const convertedDateTime = new Date(`${appointment.appointment_date}T${convertedTime}`);
        
        if (originalDate.getDate() !== convertedDateTime.getDate()) {
          appointmentDateTime = convertedDateTime;
        }
      } catch (error) {
        console.warn('Failed to convert appointment time to patient timezone:', error);
        // Fall back to original provider timezone display
        if (appointment.provider_timezone) {
          displayTimezone = getTimezoneDisplay(appointment.provider_timezone);
        }
      }
    } else if (appointment.provider_timezone) {
      // Show provider timezone if patient timezone not available
      displayTimezone = getTimezoneDisplay(appointment.provider_timezone);
    }

    const formattedDate = appointmentDateTime.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    // Format display time to 12-hour format
    const formatTime = (timeStr: string) => {
      try {
        const [hours, minutes] = timeStr.split(':');
        const hour24 = parseInt(hours, 10);
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const ampm = hour24 >= 12 ? 'PM' : 'AM';
        return `${hour12}:${minutes} ${ampm}`;
      } catch {
        return timeStr;
      }
    };

    return (
      <div className="flex flex-col">
        <span className="text-sm font-medium">{formattedDate}</span>
        {displayTime && (
          <span className="text-xs text-muted-foreground">
            {formatTime(displayTime)}
            {displayTimezone && (
              <span className="block text-xxs opacity-75">
                {displayTimezone}
              </span>
            )}
          </span>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className={cn("w-full p-6", className)}>
        {/* Title and Subtitle Loading */}
        {(title || subtitle) && (
          <div className="space-y-2 mb-6">
            {title && <div className="h-9 bg-muted animate-pulse rounded-md w-64" />}
            {subtitle && <div className="h-5 bg-muted animate-pulse rounded-md w-96" />}
          </div>
        )}
        
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-4">
            {showSearch && (
              <div className="max-w-sm h-10 bg-muted animate-pulse rounded-md" />
            )}
          </div>
          {showAddButton && (
            <div className="w-32 h-10 bg-muted animate-pulse rounded-md" />
          )}
        </div>
        <div className="border rounded-lg overflow-hidden">
          <div className="h-12 bg-muted animate-pulse" />
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 border-t bg-background animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div ref={mainContainerRef} className={cn("w-full overflow-hidden", className)}>
      {/* Title and Subtitle */}
      {(title || subtitle) && (
        <div className="space-y-2 mb-6 px-6">
          {title && <h1 className="text-3xl font-bold">{title}</h1>}
          {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
        </div>
      )}
      
      {/* Header with search and add button */}
      <div className="flex items-center justify-between py-4 px-6">
        <div className="flex items-center gap-4">
          {showSearch && (
            <Input
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          )}
        </div>
        {showAddButton && onAddPatient && (
          <Button onClick={onAddPatient} style={{ display: 'none' }}>
            {addButtonLabel}
          </Button>
        )}
      </div>

      {/* Results count */}
      {showResultsCount && (
        <div className="text-sm text-muted-foreground pb-4 px-6">
          Showing {filteredPatients.length} of {patients.length} patients
        </div>
      )}

      {/* Table - Full width with horizontal scrolling */}
      <div className="w-full overflow-x-auto border-l border-r">
        <div 
          className="overflow-x-auto"
          style={{ width: `${tableWidth}px`, minWidth: '100%' }}
        >
        <Table className="min-w-[800px]">
          <TableHeader>
            <TableRow>
              <TableHead>Patient</TableHead>
              <TableHead>Intake Status</TableHead>
              <TableHead>Providers</TableHead>
              <TableHead>Previous Visit</TableHead>
              <TableHead>Next Visit</TableHead>
              <TableHead>Joined</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredPatients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <div className="text-muted-foreground">
                    {searchTerm ? "No patients match your search." : emptyStateText}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              paginatedPatients.map((patient) => (
                <TableRow key={patient.id} className="cursor-pointer hover:bg-muted/50" onClick={() => onPatientClick?.(patient)}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-primary hover:underline">
                        {formatName(patient.first_name, patient.last_name)}
                      </span>
                      <span className="text-sm text-muted-foreground">{patient.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getIntakeStatus(patient.has_completed_intake)}
                  </TableCell>
                  <TableCell>
                    {getProvidersList(patient.assigned_providers)}
                  </TableCell>
                  <TableCell>
                    {formatAppointment(patient.previous_appointment)}
                  </TableCell>
                  <TableCell>
                    {formatAppointment(patient.next_appointment)}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {formatDate(patient.created_at)}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between space-x-2 py-4 px-6">
        <div className="text-sm text-muted-foreground">
          {filteredPatients.length > 0 
            ? `Showing ${currentPage * pageSize + 1} to ${Math.min((currentPage + 1) * pageSize, filteredPatients.length)} of ${filteredPatients.length} patients`
            : "No patients to display"
          }
        </div>
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => {
                setPageSize(Number(value))
                setCurrentPage(0)
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                {[4, 8, 10, 20].map((size) => (
                  <SelectItem key={size} value={`${size}`}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-[100px] items-center justify-center text-sm font-medium">
            Page {currentPage + 1} of {totalPages}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
            >
              <span className="sr-only">Go to first page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 0}
            >
              <span className="sr-only">Go to previous page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <span className="sr-only">Go to next page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <span className="sr-only">Go to last page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}